import { Box, Stack, Typography, Link as MuiLink, styled } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Link as RouterLink } from "react-router-dom";
import { formatLabel } from "@/utils/format-label";
import { PENDING_ACTION_LINKS, PENDING_ACTION_LABELS } from "../../data";

const ActionRow = styled(Box, {
  shouldForwardProp: (p) => p !== "blocking",
})(({ theme, blocking }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.spacing(2),
  padding: theme.spacing(1.5),
  borderRadius: 8,
  border: `1px solid ${blocking ? theme.palette.error.light : theme.palette.divider}`,
  backgroundColor: blocking ? "rgba(211, 47, 47, 0.04)" : "transparent",
}));

// The panel is rendered from the detail payload, which the backend re-checks
// on every request — so it is never blank-while-loading: the parent holds the
// loader until this data is in hand.
const PendingActionsPanel = ({ actions = [] }) => {
  if (actions.length === 0) {
    return (
      <Stack direction="row" alignItems="center" spacing={1} sx={{ color: "success.main", py: 1 }}>
        <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />
        <Typography variant="body2" fontWeight={500}>
          No pending actions on this account.
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={1}>
      {actions.map((action, idx) => {
        const label = PENDING_ACTION_LABELS[action.type] ?? formatLabel(action.type);
        const href = PENDING_ACTION_LINKS[action.type]?.(action) ?? null;

        return (
          <ActionRow key={action.referenceId ?? `${action.type}-${idx}`} blocking={action.blocking}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
              {action.blocking && <ErrorOutlineIcon sx={{ fontSize: 18, color: "error.main" }} />}
              <Box sx={{ minWidth: 0 }}>
                <Typography fontSize="0.875rem" fontWeight={500}>
                  {label}
                </Typography>
                <Typography fontSize="0.78rem" color="text.secondary" noWrap>
                  {formatLabel(action.status) ?? "—"}
                  {action.referenceId ? ` • ${action.referenceId}` : ""}
                  {action.description ? ` • ${action.description}` : ""}
                </Typography>
              </Box>
            </Stack>

            {href && (
              <MuiLink
                component={RouterLink}
                to={href}
                sx={{ display: "flex", alignItems: "center", gap: 0.5, fontSize: "0.82rem", fontWeight: 600, flexShrink: 0 }}
              >
                View <OpenInNewIcon sx={{ fontSize: 14 }} />
              </MuiLink>
            )}
          </ActionRow>
        );
      })}
    </Stack>
  );
};

export default PendingActionsPanel;